import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useRef } from 'react';
import authedFetch from '../lib/authedFetch';
import { useNavigate } from 'react-router-dom';
import {
  Upload, Download, FileSpreadsheet, Calculator, ChevronDown, ChevronUp,
  Info, AlertCircle, CheckCircle, Loader2, Database, Target, BarChart3,
  TrendingUp, TrendingDown, Minus, FileText, Gauge, BookOpen, Sparkles, Layers
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import QuickImportWizard from '../components/QuickImportWizard';
import BulkQuickImportWizard from '../components/BulkQuickImportWizard';
import DataHealthWidget from '../components/DataHealthWidget';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pillar color mapping
const PILLAR_COLORS = {
  P1: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', badge: 'bg-blue-100' },
  P2: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', badge: 'bg-emerald-100' },
  P3: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', badge: 'bg-purple-100' },
  P4: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', badge: 'bg-amber-100' },
  P5: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', badge: 'bg-rose-100' }
};

// Status colors
const STATUS_COLORS = {
  excellent: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  good: { bg: 'bg-blue-100', text: 'text-blue-700' },
  needs_improvement: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  no_data: { bg: 'bg-gray-100', text: 'text-gray-500' }
};

// Component Breakdown Card
const ComponentBreakdown = ({ measure, expanded, onToggle }) => {
  const pillarStyle = PILLAR_COLORS[measure.measure?.pillar] || PILLAR_COLORS.P1;
  
  return (
    <div className={`border rounded-lg overflow-hidden ${pillarStyle.border}`}>
      <button
        onClick={onToggle}
        className={`w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors ${pillarStyle.bg}`}
        data-testid={`measure-breakdown-${measure.measure?.id}`}
      >
        <div className="flex items-center gap-3">
          <Badge className={pillarStyle.badge}>{measure.measure?.pillar}</Badge>
          <div className="text-left">
            <h4 className="font-semibold text-gray-900">{measure.measure?.name}</h4>
            <p className="text-xs text-gray-500">{measure.measure?.pillar_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {measure.has_data ? (
            <>
              <div className="text-right">
                <span className="text-2xl font-bold text-gray-900">
                  {measure.calculated_score?.toFixed(1)}
                </span>
                <span className="text-gray-500">/100</span>
              </div>
              <div className="text-xs text-gray-500">
                {measure.data_completeness?.toFixed(0)}% complete
              </div>
            </>
          ) : (
            <Badge variant="outline" className="text-gray-400">No data</Badge>
          )}
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>
      
      {expanded && (
        <div className="p-4 bg-white border-t space-y-4">
          {/* Formula */}
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
            <div className="flex items-center gap-2 text-blue-800 text-sm font-medium mb-1">
              <Calculator size={16} />
              Calculation Formula
            </div>
            <p className="text-blue-900 font-mono text-sm">{measure.measure?.formula}</p>
          </div>
          
          {/* Component Details */}
          <div className="space-y-2">
            <h5 className="text-sm font-semibold text-gray-700">Component Breakdown</h5>
            <div className="grid gap-2">
              {measure.components?.map((comp) => {
                const statusStyle = STATUS_COLORS[comp.status] || STATUS_COLORS.no_data;
                return (
                  <div
                    key={comp.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{comp.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {(comp.weight * 100).toFixed(0)}% weight
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{comp.description}</p>
                      {comp.benchmark_low && comp.benchmark_high && (
                        <p className="text-xs text-gray-400 mt-1">
                          Benchmark: {comp.benchmark_low} - {comp.benchmark_high}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-4 ml-4">
                      {comp.value !== null ? (
                        <>
                          <div className="text-right">
                            <span className="text-lg font-bold text-gray-900">
                              {comp.value?.toFixed(1)}
                            </span>
                            <p className="text-xs text-gray-500">
                              contributes {comp.contribution?.toFixed(1)} pts
                            </p>
                          </div>
                          <Badge className={`${statusStyle.bg} ${statusStyle.text}`}>
                            {comp.status?.replace('_', ' ')}
                          </Badge>
                        </>
                      ) : (
                        <Badge variant="outline" className="text-gray-400">
                          No data
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Last Updated */}
          {measure.last_updated && (
            <p className="text-xs text-gray-400 text-right">
              Last updated: {new Date(measure.last_updated).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Import Result Display
const ImportResult = ({ result }) => {
  if (!result) return null;
  
  const isSuccess = result.success;
  
  return (
    <Alert className={isSuccess ? 'border-emerald-500 bg-emerald-50' : 'border-rose-500 bg-rose-50'}>
      {isSuccess ? (
        <CheckCircle className="h-4 w-4 text-emerald-600" />
      ) : (
        <AlertCircle className="h-4 w-4 text-rose-600" />
      )}
      <AlertDescription>
        <div className={isSuccess ? 'text-emerald-800' : 'text-rose-800'}>
          {result.message || (isSuccess ? 'Import successful!' : 'Import failed')}
        </div>
        
        {/* Success Details */}
        {isSuccess && result.calculation_result && (
          <div className="mt-3 p-3 bg-white rounded border border-emerald-200">
            <p className="font-medium text-emerald-900">
              Calculated Score: {result.calculation_result.calculated_score?.toFixed(1)}
            </p>
            <p className="text-sm text-emerald-700 mt-1">
              Data Completeness: {result.calculation_result.data_completeness?.toFixed(0)}%
            </p>
          </div>
        )}
        
        {/* Errors */}
        {result.errors && result.errors.length > 0 && (
          <div className="mt-3">
            <p className="font-medium text-sm text-rose-800">Errors:</p>
            <ul className="list-disc list-inside text-sm mt-1 text-rose-700">
              {result.errors.slice(0, 5).map((err, i) => (
                <li key={`err-${err.slice(0, 25)}-${i}`}>{err}</li>
              ))}
              {result.errors.length > 5 && (
                <li className="text-rose-600 font-medium">
                  ...and {result.errors.length - 5} more errors
                </li>
              )}
            </ul>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
};

export default function ComponentDataImportPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // State
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('formulas');
  const [formulas, setFormulas] = useState([]);
  const [selectedMeasure, setSelectedMeasure] = useState(null);
  const [selectedPillar, setSelectedPillar] = useState('all');
  const [expandedMeasures, setExpandedMeasures] = useState({});
  const [measureBreakdowns, setMeasureBreakdowns] = useState({});
  
  // Import state
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importType, setImportType] = useState('aggregated');
  
  // Quick Import Wizard state
  const [showQuickImport, setShowQuickImport] = useState(false);
  
  // Bulk Import Wizard state
  const [showBulkImport, setShowBulkImport] = useState(false);
  
  // Segment filter state
  const [cohorts, setCohorts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [filterCohortId, setFilterCohortId] = useState('all');
  const [filterProgramId, setFilterProgramId] = useState('all');
  
  // Fetch formulas and segments on mount
  useEffect(() => {
    fetchFormulas();
    fetchSegments();
  }, []);
  
  // Fetch all measure breakdowns when switching to breakdown tab
  useEffect(() => {
    if (activeTab === 'breakdown' && formulas.length > 0) {
      fetchAllMeasureBreakdowns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, formulas.length]);
  
  const fetchAllMeasureBreakdowns = async () => {
    const token = secureStorage.getItem('session_token');
    if (!token) return;
    
    // Fetch breakdowns for all measures in parallel
    const fetchPromises = formulas.map(async (formula) => {
      try {
        const response = await authedFetch(`${API_URL}/api/lei-components/breakdown/${formula.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          return { measureId: formula.id, data };
        }
      } catch (error) {
        logger.error(`Failed to fetch breakdown for ${formula.id}:`, error);
      }
      return null;
    });
    
    const results = await Promise.all(fetchPromises);
    
    // Batch update state with all results
    const newBreakdowns = {};
    results.forEach(result => {
      if (result) {
        newBreakdowns[result.measureId] = result.data;
      }
    });
    
    if (Object.keys(newBreakdowns).length > 0) {
      setMeasureBreakdowns(newBreakdowns);
    }
  };
  
  const fetchSegments = async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const [cohortsRes, programsRes] = await Promise.all([
        authedFetch(`${API_URL}/api/segments/cohorts`, { headers: { 'Authorization': `Bearer ${token}` } }),
        authedFetch(`${API_URL}/api/segments/programs`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (cohortsRes.ok) {
        const data = await cohortsRes.json();
        setCohorts(data.cohorts || []);
      }
      if (programsRes.ok) {
        const data = await programsRes.json();
        setPrograms(data.programs || []);
      }
    } catch (error) {
      logger.error('Failed to fetch segments:', error);
    }
  };
  
  const fetchFormulas = async () => {
    setLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const response = await authedFetch(`${API_URL}/api/lei-components/formulas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormulas(data.measures || []);
      }
    } catch (error) {
      logger.error('Failed to fetch formulas:', error);
      toast.error('Failed to load formulas');
    }
    setLoading(false);
  };
  
  const fetchMeasureBreakdown = async (measureId) => {
    try {
      const token = secureStorage.getItem('session_token');
      const response = await authedFetch(`${API_URL}/api/lei-components/breakdown/${measureId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMeasureBreakdowns(prev => ({ ...prev, [measureId]: data }));
      }
    } catch (error) {
      logger.error(`Failed to fetch breakdown for ${measureId}:`, error);
    }
  };
  
  const handleToggleExpand = async (measureId) => {
    const isExpanding = !expandedMeasures[measureId];
    setExpandedMeasures(prev => ({
      ...prev,
      [measureId]: isExpanding
    }));
    
    // Fetch breakdown data if expanding and not already fetched
    if (isExpanding && !measureBreakdowns[measureId]) {
      await fetchMeasureBreakdown(measureId);
    }
  };
  
  const handleCSVUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    setImportResult(null);
    
    try {
      const token = secureStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await authedFetch(
        `${API_URL}/api/lei-components/import-csv?import_type=${importType}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );
      
      const data = await response.json();
      setImportResult(data);
      
      if (data.success) {
        toast.success(`Successfully imported ${data.measures_imported} measure(s)!`);
        // Refresh breakdowns
        data.results?.forEach(r => {
          setMeasureBreakdowns(prev => ({
            ...prev,
            [r.measure_id]: {
              measure: { id: r.measure_id, name: r.measure_name, pillar: r.pillar, pillar_name: r.pillar_name, formula: r.formula },
              has_data: true,
              calculated_score: r.calculated_score,
              data_completeness: r.data_completeness,
              components: r.components
            }
          }));
        });
      } else {
        toast.error('Import completed with errors');
      }
    } catch (error) {
      logger.error('CSV upload error:', error);
      toast.error('Failed to upload CSV');
      setImportResult({
        success: false,
        errors: [error.message]
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const generateCSVTemplate = () => {
    // Generate a CSV template with all component IDs
    const headers = ['measure_id', 'component_id', 'value', 'period'];
    const rows = [headers.join(',')];
    
    formulas.forEach(measure => {
      measure.components?.forEach(comp => {
        rows.push(`${measure.id},${comp.id},,`);
      });
    });
    
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'lei_component_data_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('Template downloaded!');
  };
  
  // Group formulas by pillar
  const groupedFormulas = formulas.reduce((acc, formula) => {
    const pillar = formula.pillar;
    if (!acc[pillar]) {
      acc[pillar] = { pillar, pillar_name: formula.pillar_name, measures: [] };
    }
    acc[pillar].measures.push(formula);
    return acc;
  }, {});
  
  // Filter formulas by selected pillar
  const filteredFormulas = selectedPillar === 'all'
    ? formulas
    : formulas.filter(f => f.pillar === selectedPillar);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }
  
  const __pageTheme = getLightSectionTheme('Navy');
  const __heroActions = (
    <>
      <Button
        onClick={() => setShowQuickImport(true)}
        className={`${__pageTheme.actionBtn} h-9`}
        data-testid="quick-import-btn"
      >
        <Sparkles size={16} className="mr-2" />
        Quick Import
      </Button>
      <Button
        onClick={() => setShowBulkImport(true)}
        className="bg-white border border-slate-200 text-slate-900 hover:bg-white border-0 h-9"
        data-testid="bulk-import-btn"
      >
        <Layers size={16} className="mr-2" />
        Bulk Import
      </Button>
      <Button
        onClick={() => navigate('/lei-dashboard')}
        variant="outline"
        className="bg-transparent text-slate-700 border-white/50 hover:bg-slate-50 h-9"
      >
        <Gauge size={16} className="mr-2" />
        Dashboard
      </Button>
    </>
  );
  const __totalComponents = formulas.reduce((sum, f) => sum + (f.component_count || 0), 0);
  const __heroStats = [
    {
      label: 'Total Measures',
      value: formulas.length,
      testId: 'cdi-stat-measures',
      whyPanel: {
        label: 'Total Measures',
        value: formulas.length,
        valueSuffix: 'measures',
        description: 'Count of LEI measures that have a defined component formula in the system. Each measure aggregates raw component scores (e.g., assessment, completion, retention) into a single LEI signal.',
        formula: 'count(LEI measures WHERE has_formula)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'A higher number means broader coverage of learning signals. Most mature programs operate with 8–14 measures across the 4 pillars.' },
          { label: 'What to do', value: '', hint: formulas.length === 0
            ? 'No measures defined yet — visit Custom Measures to seed your first LEI definitions, then return here to import their components.'
            : formulas.length < 4
              ? 'Coverage is thin. Add at least one measure per pillar (Acquire, Apply, Sustain, Strategic) so your LEI score reflects the full learning lifecycle.'
              : 'Solid coverage. Use the tabs below to import or refresh component data for each measure.' },
        ],
        links: [
          { label: 'Manage Custom Measures', path: '/custom-measures' },
          { label: 'Open LEI Dashboard', path: '/lei-dashboard' },
        ],
      },
    },
    {
      label: 'Pillars',
      value: Object.keys(groupedFormulas).length,
      testId: 'cdi-stat-pillars',
      whyPanel: {
        label: 'Pillars',
        value: Object.keys(groupedFormulas).length,
        valueSuffix: 'pillars',
        description: 'Number of distinct LEI pillars (e.g., Acquire, Apply, Sustain, Strategic) currently represented by your defined measures. Pillar diversity is what makes the LEI score balanced.',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Aim for 4 — one per LEI pillar. If you have fewer, your overall LEI will be skewed toward whichever pillars are over-represented.' },
          { label: 'What to do', value: '', hint: Object.keys(groupedFormulas).length < 4
            ? 'Add measures for the missing pillars in Custom Measures so every dimension of learning effectiveness contributes to your score.'
            : 'Full pillar coverage. Focus on data quality — make sure each pillar has fresh component data imported.' },
        ],
        links: [
          { label: 'Manage Custom Measures', path: '/custom-measures' },
        ],
      },
    },
    {
      label: 'Total Components',
      value: __totalComponents,
      testId: 'cdi-stat-components',
      whyPanel: {
        label: 'Total Components',
        value: __totalComponents,
        valueSuffix: 'components',
        description: 'Total number of raw component inputs (assessment scores, completion rates, transfer rates, etc.) wired into your LEI measures. These are the data points you import on this page.',
        formula: 'sum(component_count) across all measures',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'More components per measure = more reliable signal, but also more data to keep fresh. Most mature programs sit around 3–5 components per measure.' },
          { label: 'What to do', value: '', hint: __totalComponents === 0
            ? 'No components yet. Open the Import tab below to upload your first dataset (assessment scores, course completions, etc.).'
            : 'Use the Component Import tab to refresh stale components — anything older than 90 days will weaken your LEI signal.' },
        ],
        links: [
          { label: 'Open Data Management', path: '/data-management' },
        ],
      },
    },
  ];
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Component Data Import"
        description="Import raw component data (e.g., assessment scores, pass rates) and let the system calculate your LEI measure scores automatically with full formula transparency."
        theme={__pageTheme}
        icon={Calculator}
        actions={__heroActions}
        stats={__heroStats}
        testId="component-data-import-hero"
      />
    )}>
    <div data-testid="component-data-import-page">
      <div className="space-y-6">
        
        {/* Data Health Overview */}
        <DataHealthWidget />
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 h-auto p-1 bg-slate-100">
            <TabsTrigger
              value="formulas"
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white"
            >
              <BookOpen className="h-4 w-4" />
              <div className="text-left">
                <div className="font-medium">Formula Reference</div>
                <div className="text-xs text-slate-500">View all calculation formulas</div>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="import"
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white"
            >
              <Upload className="h-4 w-4" />
              <div className="text-left">
                <div className="font-medium">Import Data</div>
                <div className="text-xs text-slate-500">Upload component values</div>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="breakdown"
              className="flex items-center gap-2 py-3 data-[state=active]:bg-white"
            >
              <BarChart3 className="h-4 w-4" />
              <div className="text-left">
                <div className="font-medium">Score Breakdown</div>
                <div className="text-xs text-slate-500">View calculated scores</div>
              </div>
            </TabsTrigger>
          </TabsList>
          
          {/* Formula Reference Tab */}
          <TabsContent value="formulas" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-indigo-600" />
                  LEI Measure Formulas
                </CardTitle>
                <CardDescription>
                  Each LEI measure is calculated from weighted components. Import raw component 
                  data and the system will apply these formulas automatically.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Pillar Filter */}
                <div className="mb-6">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Filter by Pillar
                  </label>
                  <Select value={selectedPillar} onValueChange={setSelectedPillar}>
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="All Pillars" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Pillars</SelectItem>
                      {Object.entries(groupedFormulas).sort().map(([pillar, data]) => (
                        <SelectItem key={pillar} value={pillar}>
                          {pillar}: {data.pillar_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Formula Cards */}
                <div className="space-y-4">
                  {filteredFormulas.map((formula) => {
                    const pillarStyle = PILLAR_COLORS[formula.pillar] || PILLAR_COLORS.P1;
                    return (
                      <div
                        key={formula.id}
                        className={`border rounded-lg p-4 ${pillarStyle.bg} ${pillarStyle.border}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={pillarStyle.badge}>
                                {formula.pillar}
                              </Badge>
                              <h4 className="font-semibold text-gray-900">
                                {formula.name}
                              </h4>
                            </div>
                            <p className="text-sm text-gray-600">{formula.pillar_name}</p>
                          </div>
                          <Badge variant="outline">
                            {formula.component_count} components
                          </Badge>
                        </div>
                        
                        {/* Formula */}
                        <div className="bg-white rounded-lg p-3 mb-3 border">
                          <p className="text-xs text-gray-500 mb-1">Formula</p>
                          <p className="font-mono text-sm text-indigo-700">{formula.formula}</p>
                        </div>
                        
                        {/* Components */}
                        <div className="grid gap-2">
                          {formula.components?.map((comp) => (
                            <div
                              key={comp.id}
                              className="bg-white rounded p-2 border flex items-center justify-between"
                            >
                              <div className="flex-1">
                                <span className="font-medium text-gray-800 text-sm">
                                  {comp.name}
                                </span>
                                <span className="text-gray-400 mx-2">|</span>
                                <span className="text-xs text-gray-500">
                                  {comp.description}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 ml-4">
                                <Badge variant="outline" className="text-xs">
                                  {(comp.weight * 100).toFixed(0)}%
                                </Badge>
                                {comp.benchmark_low && (
                                  <span className="text-xs text-gray-400">
                                    Benchmark: {comp.benchmark_low}-{comp.benchmark_high}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Import Tab */}
          <TabsContent value="import" className="space-y-6">
            {/* Info Card */}
            <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-indigo-800">
                  <Info className="h-5 w-5" />
                  How Component Import Works
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700">
                  Instead of importing pre-calculated LEI scores, you can now import the raw 
                  component data (e.g., assessment scores, participation rates) and let the 
                  system calculate the final scores using standardized formulas.
                </p>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-white rounded-lg p-4 border border-indigo-100">
                    <h4 className="font-semibold text-indigo-800 mb-2">Aggregated Import</h4>
                    <p className="text-sm text-gray-600">
                      Import already-averaged values (e.g., avg assessment score = 85).
                      Best for summarized data from your LMS.
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-indigo-100">
                    <h4 className="font-semibold text-indigo-800 mb-2">Individual Import</h4>
                    <p className="text-sm text-gray-600">
                      Import individual records (e.g., each learner's score). The system 
                      will calculate averages automatically.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Upload Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-indigo-600" />
                  Import Component Data
                </CardTitle>
                <CardDescription>
                  Download the template, fill in your component values, and upload
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Import Type Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Import Type
                  </label>
                  <Select value={importType} onValueChange={setImportType}>
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aggregated">
                        Aggregated (pre-averaged values)
                      </SelectItem>
                      <SelectItem value="individual">
                        Individual (raw records)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    variant="outline"
                    onClick={generateCSVTemplate}
                    className="flex items-center gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid="download-component-template-btn"
                  >
                    <Download className="h-4 w-4" />
                    Download CSV Template
                  </Button>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="hidden"
                    data-testid="component-file-input"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700"
                    data-testid="upload-component-file-btn"
                  >
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {isUploading ? 'Uploading...' : 'Upload CSV'}
                  </Button>
                </div>
                
                {/* Upload Result */}
                <ImportResult result={importResult} />
                
                {/* CSV Format Help */}
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    CSV Format
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Your CSV should have the following columns:
                  </p>
                  <div className="bg-white rounded border p-3 font-mono text-sm overflow-x-auto">
                    <p className="text-gray-500">measure_id,component_id,value,period</p>
                    <p>training_performance,assessment_score,85.5,2025-Q1</p>
                    <p>training_performance,pass_rate,92.0,2025-Q1</p>
                    <p>training_performance,mastery_index,45.0,2025-Q1</p>
                  </div>
                </div>
                
                {/* Sample Data Download */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Download Sample Data
                  </h4>
                  <p className="text-sm text-amber-700 mb-3">
                    Download pre-filled sample CSV files to test the import process:
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="/sample-data/sample_lei_components_q1_2026.csv"
                      download="sample_lei_components_q1_2026.csv"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-amber-300 rounded-lg text-amber-800 hover:bg-amber-50 text-sm font-medium transition-colors"
                      data-testid="download-sample-q1"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Q1 2026 Sample (Aggregated)
                    </a>
                    <a
                      href="/sample-data/sample_lei_components_q2_2026.csv"
                      download="sample_lei_components_q2_2026.csv"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-amber-300 rounded-lg text-amber-800 hover:bg-amber-50 text-sm font-medium transition-colors"
                      data-testid="download-sample-q2"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Q2 2026 Sample (Aggregated)
                    </a>
                    <a
                      href="/sample-data/sample_individual_scores.csv"
                      download="sample_individual_scores.csv"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-orange-300 rounded-lg text-orange-800 hover:bg-orange-50 text-sm font-medium transition-colors"
                      data-testid="download-sample-individual"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Individual Scores Sample
                    </a>
                  </div>
                  <p className="text-xs text-amber-600 mt-2">
                    Tip: Import Q1 first, then Q2 to see trend data in the breakdown view.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Breakdown Tab */}
          <TabsContent value="breakdown" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-indigo-600" />
                  Measure Score Breakdown
                </CardTitle>
                <CardDescription>
                  View how each LEI measure score is calculated from its components. 
                  Click on a measure to see the detailed breakdown.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex flex-wrap items-center gap-4 mb-6">
                  {/* Pillar Filter */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">
                      Pillar
                    </label>
                    <Select value={selectedPillar} onValueChange={setSelectedPillar}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="All Pillars" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Pillars</SelectItem>
                        {Object.entries(groupedFormulas).sort().map(([pillar, data]) => (
                          <SelectItem key={pillar} value={pillar}>
                            {pillar}: {data.pillar_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Cohort Filter */}
                  {cohorts.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Cohort
                      </label>
                      <Select value={filterCohortId} onValueChange={setFilterCohortId}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="All Cohorts" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Cohorts</SelectItem>
                          {cohorts.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Program Filter */}
                  {programs.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Program
                      </label>
                      <Select value={filterProgramId} onValueChange={setFilterProgramId}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="All Programs" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Programs</SelectItem>
                          {programs.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                {/* Measure Breakdowns */}
                <div className="space-y-3">
                  {filteredFormulas.map((formula) => {
                    const breakdown = measureBreakdowns[formula.id] || {
                      measure: formula,
                      has_data: false,
                      calculated_score: null,
                      components: formula.components?.map(c => ({
                        ...c,
                        value: null,
                        contribution: null,
                        status: 'no_data'
                      }))
                    };
                    
                    return (
                      <ComponentBreakdown
                        key={formula.id}
                        measure={breakdown}
                        expanded={!!expandedMeasures[formula.id]}
                        onToggle={() => handleToggleExpand(formula.id)}
                      />
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Quick Import Wizard Modal */}
      <QuickImportWizard
        isOpen={showQuickImport}
        onClose={() => setShowQuickImport(false)}
        formulas={formulas}
        onImportComplete={(result) => {
          // Refresh the measure breakdown if it was the one just imported
          if (result.calculation_result?.measure_id) {
            fetchMeasureBreakdown(result.calculation_result.measure_id);
          }
          // Switch to breakdown tab to show the result
          setActiveTab('breakdown');
        }}
      />
      
      {/* Bulk Import Wizard Modal */}
      <BulkQuickImportWizard
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        formulas={formulas}
        onImportComplete={(results) => {
          // Refresh breakdowns for all imported measures
          results.forEach(result => {
            if (result.success && result.measure_id) {
              fetchMeasureBreakdown(result.measure_id);
            }
          });
          // Switch to breakdown tab to show results
          setActiveTab('breakdown');
        }}
      />
    </div>
    </PageShell>
  );
}
